import { useCallback, useEffect, useMemo, useState } from 'react';
import dashboardService from '../services/dashboard-service';
import { buildDailyChartData, buildMonthlyChartData } from '../utils/chart-helpers';

/**
 * Custom hook encapsulating all dashboard state, data fetching, and memoized values.
 */
export default function useDashboardData() {
  const [activeTab, setActiveTab] = useState(0);
  const [dashboardData, setDashboardData] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [dailyData, setDailyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [chartView, setChartView] = useState(0);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedDailyYear, setSelectedDailyYear] = useState(
    new Date().getFullYear()
  );
  const [selectedDailyMonth, setSelectedDailyMonth] = useState(
    new Date().getMonth() + 1
  );

  const availableYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 6 }, (_, i) => currentYear - i);
  }, []);

  const availableMonths = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => ({
      value: i + 1,
      label: new Date(2024, i, 1).toLocaleDateString('en-US', {
        month: 'long'
      })
    }));
  }, []);

  const currentDate = useMemo(() => {
    return new Date().toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }, []);

  // Navigation handlers
  const handleNavigateToPackageDetails = useCallback(() => {
    window.open('/packages', '_blank');
  }, []);

  const handleNavigateToShipmentTracking = useCallback(() => {
    window.open('/zoe-shipment-tracking', '_blank');
  }, []);

  const handleNavigateToShipmentStatus = useCallback((status) => {
    window.open(
      `/zoe-shipments-status/${encodeURIComponent(status)}`,
      '_blank'
    );
  }, []);

  const handleNavigateToShipmentByDate = useCallback((dateType) => {
    window.open(
      `/zoe-shipments-date/${encodeURIComponent(dateType)}`,
      '_blank'
    );
  }, []);

  const handleNavigateToPackageByDate = useCallback((dateType) => {
    window.open(`/packages-date/${encodeURIComponent(dateType)}`, '_blank');
  }, []);

  const handleTabChange = useCallback((_event, newValue) => {
    setActiveTab(newValue);
  }, []);

  const handleChartViewChange = useCallback((_event, newValue) => {
    setChartView(newValue);
  }, []);

  const handleYearChange = useCallback((event) => {
    setSelectedYear(event.target.value);
  }, []);

  const handleDailyYearChange = useCallback((event) => {
    setSelectedDailyYear(event.target.value);
  }, []);

  const handleDailyMonthChange = useCallback((event) => {
    setSelectedDailyMonth(event.target.value);
  }, []);

  // Fetch dashboard + current-month daily stats on mount
  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        const response = await dashboardService.getDashboardStatistics();
        if (response.success) {
          setDashboardData(response.data);
        }
        const dailyResponse =
          await dashboardService.getCurrentMonthDailyStatistics();
        if (dailyResponse.success) {
          setDailyData(dailyResponse.data);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      }
      setLoading(false);
    };
    fetchDashboardData();
  }, []);

  // Fetch monthly stats when selectedYear changes
  useEffect(() => {
    const fetchMonthlyData = async () => {
      if (selectedYear) {
        setLoading(true);
        try {
          const [packageMonthlyResponse, shipmentMonthlyResponse] =
            await Promise.all([
              dashboardService.getPackageDetailsMonthlyStatistics(selectedYear),
              dashboardService.getZoeShipmentTrackingMonthlyStatistics(
                selectedYear
              )
            ]);

          if (
            packageMonthlyResponse.success &&
            shipmentMonthlyResponse.success
          ) {
            setMonthlyData({
              packageDetails: packageMonthlyResponse.data,
              zoeShipmentTracking: shipmentMonthlyResponse.data
            });
          }
        } catch (error) {
          console.error(
            'Error fetching monthly data for year:',
            selectedYear,
            error
          );
        }
        setLoading(false);
      }
    };
    fetchMonthlyData();
  }, [selectedYear]);

  // Fetch daily stats when year/month changes
  useEffect(() => {
    const fetchDailyData = async () => {
      if (selectedDailyYear && selectedDailyMonth) {
        setLoading(true);
        try {
          const [packageDailyResponse, shipmentDailyResponse] =
            await Promise.all([
              dashboardService.getPackageDetailsDailyStatistics(
                selectedDailyYear,
                selectedDailyMonth
              ),
              dashboardService.getZoeShipmentTrackingDailyStatistics(
                selectedDailyYear,
                selectedDailyMonth
              )
            ]);

          if (packageDailyResponse.success && shipmentDailyResponse.success) {
            setDailyData({
              year: selectedDailyYear,
              month: selectedDailyMonth,
              monthName: new Date(
                selectedDailyYear,
                selectedDailyMonth - 1,
                1
              ).toLocaleDateString('en-US', { month: 'long' }),
              packageDetails: packageDailyResponse.data,
              zoeShipmentTracking: shipmentDailyResponse.data
            });
          }
        } catch (error) {
          console.error(
            'Error fetching daily data for:',
            selectedDailyYear,
            selectedDailyMonth,
            error
          );
        }
        setLoading(false);
      }
    };
    fetchDailyData();
  }, [selectedDailyYear, selectedDailyMonth]);

  // Memoized chart data
  const chartData = useMemo(
    () => buildMonthlyChartData(monthlyData),
    [monthlyData]
  );
  const dailyChartData = useMemo(
    () => buildDailyChartData(dailyData),
    [dailyData]
  );

  const packageDetailsData = dashboardData?.packageDetails;
  const zoeShipmentTrackingData = dashboardData?.zoeShipmentTracking;

  return {
    // State
    activeTab,
    chartView,
    loading,
    selectedYear,
    selectedDailyYear,
    selectedDailyMonth,
    // Data
    dashboardData,
    packageDetailsData,
    zoeShipmentTrackingData,
    monthlyData,
    dailyData,
    chartData,
    dailyChartData,
    // Helpers
    availableYears,
    availableMonths,
    currentDate,
    // Handlers
    handleTabChange,
    handleChartViewChange,
    handleYearChange,
    handleDailyYearChange,
    handleDailyMonthChange,
    handleNavigateToPackageDetails,
    handleNavigateToShipmentTracking,
    handleNavigateToShipmentStatus,
    handleNavigateToShipmentByDate,
    handleNavigateToPackageByDate
  };
}
